import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FontText, SecondaryButton } from './';
import { localAnalytics } from '../../utils/analytics';

interface GlobalMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GlobalMenu: React.FC<GlobalMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isAuthenticated, signOut, userId } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleProfileClick = () => {
    localAnalytics().logEvent('GlobalMenuProfileClicked', {
      screen: 'GlobalMenu',
      action: 'ProfileClicked',
      userId,
    });
    onClose();
  };

  const handleSignInClick = () => {
    localAnalytics().logEvent('GlobalMenuSignInClicked', {
      screen: 'GlobalMenu',
      action: 'SignInClicked',
      userId: userId || 'unauthenticated',
    });
    onClose();
  };

  const handleHubsClick = () => {
    localAnalytics().logEvent('GlobalMenuHubsClicked', {
      screen: 'GlobalMenu',
      action: 'HubsClicked',
      userId,
    });
  };

  const handleTermsOfServiceClick = () => {
    localAnalytics().logEvent('GlobalMenuTermsOfServiceClicked', {
      screen: 'GlobalMenu',
      action: 'TermsOfServiceClicked',
      userId: userId || 'unauthenticated',
    });
    onClose();
  };

  const handlePrivacyPolicyClick = () => {
    localAnalytics().logEvent('GlobalMenuPrivacyPolicyClicked', {
      screen: 'GlobalMenu',
      action: 'PrivacyPolicyClicked',
      userId: userId || 'unauthenticated',
    });
    onClose();
  };

  const handleSignOutClick = () => {
    localAnalytics().logEvent('GlobalMenuSignOutClicked', {
      screen: 'GlobalMenu',
      action: 'SignOutClicked',
      userId,
    });
    signOut();
    onClose();
  };

  const handleHomeClick = () => {
    localAnalytics().logEvent('GlobalMenuHomeClicked', {
      screen: 'GlobalMenu',
      action: 'HomeClicked',
      userId,
    });
    onClose();
  };

  if (!isOpen) return null;

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      paddingTop: 45,
      paddingRight: 15,
    },
    menuContainer: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      minWidth: 200,
      overflow: 'hidden',
      border: `1px solid ${theme.colors.overlay}`,
    },
    menuSection: {
      padding: '8px 0',
    },
    menuItem: {
      display: 'block',
      width: '100%',
      padding: '12px 20px',
      background: 'none',
      border: 'none',
      color: theme.colors.text,
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      borderBottom: `1px solid ${theme.colors.overlay}`,
      textDecoration: 'none',
    },
    menuItemLast: {
      borderBottom: 'none',
    },
    menuItemHover: {
      backgroundColor: theme.colors.overlay,
    },
  };

  return (
    <div style={styles.overlay}>
      <div ref={containerRef} style={styles.menuContainer}>
        <div style={styles.menuSection}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <SecondaryButton
              style={{
                ...styles.menuItem,
                borderBottom: `1px solid ${theme.colors.overlay}`,
              }}
              onClick={handleHomeClick}
            >
              <FontText normal style={{ color: theme.colors.text }}>
                {t('books')}
              </FontText>
            </SecondaryButton>
          </Link>

          {isAuthenticated ? (
            <>
              <Link to="/profile" style={{ textDecoration: 'none' }}>
                <button
                  style={{
                    ...styles.menuItem,
                    borderBottom: `1px solid ${theme.colors.overlay}`,
                  }}
                  onClick={handleProfileClick}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      theme.colors.overlay;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <FontText normal style={{ color: theme.colors.text }}>
                    {t('profile')}
                  </FontText>
                </button>
              </Link>
            </>
          ) : (
            <Link to="/auth" style={{ textDecoration: 'none' }}>
              <button
                style={{
                  ...styles.menuItem,
                }}
                onClick={handleSignInClick}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = theme.colors.overlay;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FontText normal style={{ color: theme.colors.text }}>
                  {t('login')}
                </FontText>
              </button>
            </Link>
          )}
          <Link to="/hub" style={{ textDecoration: 'none' }}>
            <SecondaryButton
              style={{
                ...styles.menuItem,
                borderBottom: `1px solid ${theme.colors.overlay}`,
              }}
              onClick={handleHubsClick}
            >
              <FontText normal style={{ color: theme.colors.text }}>
                {t('tropes')}
              </FontText>
            </SecondaryButton>
          </Link>
          <a
            href="/terms"
            target="_blank"
            style={{
              ...styles.menuItem,
            }}
            onClick={handleTermsOfServiceClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = theme.colors.overlay;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FontText normal style={{ color: theme.colors.text }}>
              {t('terms_of_service')}
            </FontText>
          </a>
          <a
            href="/privacy"
            target="_blank"
            style={{
              ...styles.menuItem,
            }}
            onClick={handlePrivacyPolicyClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = theme.colors.overlay;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FontText normal style={{ color: theme.colors.text }}>
              {t('privacy_policy')}
            </FontText>
          </a>
          <a
            href="/support"
            target="_blank"
            style={{
              ...styles.menuItem,
            }}
            onClick={handlePrivacyPolicyClick}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = theme.colors.overlay;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FontText normal style={{ color: theme.colors.text }}>
              {t('support')}
            </FontText>
          </a>
          {isAuthenticated && (
            <button
              style={{
                ...styles.menuItem,
                ...styles.menuItemLast,
              }}
              onClick={handleSignOutClick}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = theme.colors.overlay;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FontText normal>{t('sign_out')}</FontText>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
